import type { EmergencyGrantDto } from '@shared/api/contracts';
import type { Withdrawal } from '../model/types';
import { awaitsAdminApproval, isMerchantCancellableState } from './state-view';

export type MakerKind = 'HUMAN' | 'AUTO' | 'B2B' | 'LEGACY_UNKNOWN';

export type ApprovalBlock = 'automatic' | 'maker_unknown' | 'self_approval' | 'own_funds' | 'not_awaiting';

export type GrantViewStatus = 'active' | 'consumed' | 'revoked' | 'expired';

const AUTOMATIC: MakerKind[] = ['AUTO', 'B2B'];

export const withdrawalApprovalBlock = (withdrawal: Withdrawal, actorId: string | undefined): ApprovalBlock | null => {
  if (!awaitsAdminApproval(withdrawal.state, withdrawal.status)) {
    return withdrawal.state ? 'not_awaiting' : null;
  }
  const kind = withdrawal.makerKind;
  if (kind && AUTOMATIC.includes(kind)) return 'automatic';
  if (kind === 'LEGACY_UNKNOWN' || kind !== 'HUMAN' || !withdrawal.makerUserId) return 'maker_unknown';
  if (actorId && withdrawal.makerUserId === actorId) return 'self_approval';
  if (actorId && withdrawal.userId === actorId) return 'own_funds';
  return null;
};

export const canCancelAdminWithdrawal = (withdrawal: Withdrawal): boolean => {
  if (!isMerchantCancellableState(withdrawal.state, withdrawal.status)) return false;
  const kind = withdrawal.makerKind;
  if (kind && AUTOMATIC.includes(kind)) return false;
  return true;
};

export const canApproveAdminWithdrawal = (withdrawal: Withdrawal, actorId: string | undefined): boolean =>
  awaitsAdminApproval(withdrawal.state, withdrawal.status) && withdrawalApprovalBlock(withdrawal, actorId) === null;

export const canIssueEmergencyGrant = (withdrawal: Withdrawal): boolean =>
  awaitsAdminApproval(withdrawal.state, withdrawal.status) && withdrawal.makerKind === 'HUMAN' && Boolean(withdrawal.makerUserId);

export const grantViewStatus = (grant: Pick<EmergencyGrantDto, 'status' | 'expiresAt'>, now = Date.now()): GrantViewStatus => {
  if (grant.status === 'CONSUMED') return 'consumed';
  if (grant.status === 'REVOKED') return 'revoked';
  const expires = new Date(grant.expiresAt).getTime();
  if (!Number.isFinite(expires) || expires <= now) return 'expired';
  return 'active';
};

export const canRevokeEmergencyGrant = (grant: Pick<EmergencyGrantDto, 'status' | 'expiresAt'>, now = Date.now()): boolean =>
  grantViewStatus(grant, now) === 'active';
